import React, { useState, useEffect, useRef } from "react";
import Visibility from "@/assets/icons/svg/visibility.svg?react";
import {
  formatTimestampToAgo,
  getDenmarkMediaUrlWithSeopath,
  mediaStatusTooltipText,
  mediaStatusToTextAndColor,
  openVideoInNewTab,
  useClickOutside,
} from "@/utils";
import {
  Button,
  CustomSelect,
  Modal,
  PublishedIcon,
  Tooltip,
} from "@/components";
import GeneralPlayerWrapper from "../Player/GeneralPlayerWrapper";
import { updateModal } from "@/store/app/app.slice";
import { useNavigate } from "react-router-dom";
import noImage from "@/assets/icons/svg/no-image.svg";
import { AllHostsList, GetMediaApi } from "@/database";
import {
  Edit,
  Eye,
  Send,
  View,
  EllipsisVertical,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { getConsistentColor } from "@/utils";
import { MEDIA_STATUS } from "@/constants";
import { getThumbnailUrlUtil } from "@/utils/mediaUtils";
import {
  callDenmarkArticleGetApi,
  callDenmarkDetailApi,
} from "@/store/upload/upload.action";
import { useDispatch } from "react-redux";
import { trackEvent, getTrackUserContext } from "@/utils/analytics";

function getNotificationEntity(
  notificationType: number
): "Upload" | "Publish" | "Request" {
  if (notificationType === 5) return "Upload";
  if (notificationType === 6) return "Publish";
  return "Request";
}

interface EachNotificationProps {
  message: any;
  onOpenRequestModal: (message: any) => void;
  setCurrentNotification: (message: any) => void;
  readNotification: (params: any) => Promise<any>;
  user: any;
  onViewClick?: () => void;
  productList: any;
  businessList: any;
  userList: any;
  approveServiceRequest: (id: number, service: string) => void;
  disapproveServiceRequest: (id: number) => void;
  onDeleteNotification?: (notificationId: string) => void;
  onClose?: () => void;
  allHostsList: AllHostsList;
  analyticsLocation?: "discover" | "content";
}

const EachNotification: React.FC<EachNotificationProps> = ({
  message,
  onOpenRequestModal,
  setCurrentNotification,
  onClose,
  readNotification,
  user,
  onViewClick,
  productList,
  businessList,
  approveServiceRequest,
  disapproveServiceRequest,
  onDeleteNotification,
  allHostsList,
  analyticsLocation = "content",
}) => {
  const [isRead, setIsRead] = useState(message.read);
  const [isExpanded, setIsExpanded] = useState(false);
  const [modalContent, setModalContent] = useState<React.ReactNode | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [apiMessage, setApiMessage] = useState<any>(message?.meta?.meta);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState("r");
  const [isPortrait, setIsPortrait] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const navigate = useNavigate();
  const moreOptionsRef = useRef<HTMLDivElement>(null);
  const serviceDropdownRef = useRef<HTMLDivElement>(null);
  const isDeletingRef = useRef(false);

  const notificationType = message?.meta?.type;
  const displayMessage = apiMessage;
  const isServiceRequest = notificationType === 7;
  const showExpandArrow = notificationType === 5 || isServiceRequest;
  const showViewButton = notificationType === 6 || notificationType === 0;
  const showEyeIcon = message?.type === "user_request_admin";
  const isUnread = Number(isRead) === 0;
  const dispatch = useDispatch();

  useClickOutside(moreOptionsRef, () => {
    setShowMoreOptions(false);
  });

  useClickOutside(serviceDropdownRef, () => {
    setShowServiceDropdown(false);
  });

  useEffect(() => {
    setIsRead(message.read);
  }, [message.read]);

  // Portrait detection logic similar to RenderDescription
  useEffect(() => {
    if (displayMessage && !isServiceRequest) {
      const isPortraitVideo =
        displayMessage?.meta?.height > displayMessage?.meta?.width;
      const imageSource =
        isPortraitVideo && displayMessage?.images?.portrait
          ? displayMessage.images.portrait
          : displayMessage?.images?.thumb;

      if (imageSource) {
        const img = new Image();
        img.src = getThumbnailUrlUtil({
          media: displayMessage,
          id: imageSource,
          source: displayMessage?.source,
        });
        img.onload = () => {
          const isPortraitImage = img.naturalHeight > img.naturalWidth;
          setIsPortrait(isPortraitImage);
        };
        img.onerror = () => {
          setIsPortrait(false);
        };
      } else {
        setIsPortrait(false);
      }
    }
  }, [displayMessage, isServiceRequest]);

  const getProductName = (productId: number) => {
    const product = productList?.find(
      (product: any) => product?.id === productId
    );
    return product?.name;
  };

  const getBusinessName = (businessId: number) => {
    const business = businessList?.find(
      (business: any) => business?.id === businessId
    );
    return business?.name;
  };

  const getAvatarName = (): string => {
    switch (notificationType) {
      case 0:
        return message.meta.meta.name || "";
      case 5:
        return message.meta.meta.ctbydata?.name || "";
      case 7:
        return message.meta.meta.name || "";
      default:
        return "";
    }
  };

  const getAvatarBgColor = (): string => {
    switch (notificationType) {
      case 5: {
        const userName = message.meta?.meta?.ctbydata?.name || "unknown";
        return getConsistentColor(userName);
      }
      case 6:
        return "rgba(0,0,0,0.2)";
      default:
        return "#FFC109";
    }
  };

  const getAvatarInitials = (): string => {
    const name = getAvatarName();
    if (!name) return "";
    const nameParts = name
      .trim()
      .split(" ")
      .filter((part) => part.length > 0);

    if (nameParts.length >= 2) {
      return `${nameParts[0].charAt(0).toUpperCase()} ${nameParts[1]
        .charAt(0)
        .toUpperCase()}`;
    } else if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }

    return "";
  };

  const getAccessMessage = (
    state: number | undefined,
    type: number,
    status: number
  ): string | JSX.Element => {
    const mtName = message.meta.meta.modified_by;
    switch (type) {
      case 0:
        // all user onboarding notifications
        switch (status) {
          case 0:
            return `${
              message.meta.meta.name
            } has asked to onboard on the platform for ${getProductName(
              message.meta.meta.team
            )}`;
          case 3:
            return `${mtName} has declined request for ${message.meta.meta.name}`;
          case 4:
            return `${mtName} has approved request for ${message.meta.meta.name}`;
          default:
            return "";
        }
      case 7:
        switch (status) {
          case 7:
            // all service request notifications
            return `${message.meta.meta.name} requested for service ${
              message.meta.meta.service.split(".")[0]
            }`;
          case 8:
            return `${message.meta.meta.name} service request has been declined`;
          case 9:
            return `${message.meta.meta.name} service request has been approved`;
          default:
            return "";
        }
      case 5: {
        //All media upload notifications
        const title = message.meta.meta.title;
        const createdBy = message.meta.meta.ctbydata?.name;
        let suffix = "";
        let case1Processed = false;
        switch (state) {
          case 1:
            case1Processed = true;
            suffix = " has uploaded a video ";
            break;
          case 2:
            if (!case1Processed) {
              suffix = " has uploaded a video ";
            }
            break;
          case 3:
            suffix = " has uploaded a video ";
            break;
          case 10:
            return (
              <>
                <span className="text-body-emphasis text-capitalize">
                  {title}
                </span>
                <span className="text-body-emphasis"> uploaded by </span>
                <span className="text-body-emphasis text-capitalize">
                  {createdBy}
                </span>
                <span className="text-body-emphasis"> has been processed.</span>
              </>
            );
          case -2:
            suffix = " has uploaded a video ";
            break;
          case -1:
            suffix = " has uploaded a video ";
            break;
          default:
            suffix = "";
        }
        return (
          <>
            <span className="text-body-emphasis text-capitalize">
              {createdBy}
            </span>
            <span className="text-body-emphasis">{suffix}</span>
            <span className="text-body-emphasis text-capitalize">{title}</span>
          </>
        );
      }
      case 6: {
        const title = message.meta.meta.title;
        const status = message.meta.meta.status;
        const job_type = message.meta.meta.channel_name;
        let suffix = "";
        switch (status) {
          case 2:
            suffix = " has begun uploading on ";
            break;
          case 10:
            suffix = " has been successfully publish on ";
            break;
          case -2:
            suffix = " has failed to publish on ";
            break;
          case -1:
            suffix = " has failed to publish on ";
            break;
          default:
            suffix = "";
        }
        return (
          <>
            <span className="text-body-emphasis text-capitalize">{title}</span>
            <span className="text-body-emphasis">{suffix}</span>
            <span className="text-body-emphasis text-capitalize">
              {job_type}
            </span>
          </>
        );
        // return `${title}${suffix} ${job_type}`;
      }
      default:
        //All other notifications
        return `You have a new message`;
    }
  };

  const handleVisibility = async () => {
    trackEvent("browse", {
      category: "notifications",
      entity: getNotificationEntity(notificationType),
      action: "View",
      location: analyticsLocation,
      ...getTrackUserContext(),
    });
    setCurrentNotification(message);
    if (!isRead) {
      try {
        const res = await readNotification({ id: message.id, read: 1 });
        if (res && res.status === 200) {
          setIsRead(true);
        }
      } catch (error) {
        console.error("Error updating notification:", error);
      }
    }
    onOpenRequestModal(message);
  };

  useEffect(() => {
    if (message?.meta?.meta?.service?.length > 0) {
      const service = message?.meta?.meta?.service.split(".");
      const servicePermission = service[1];
      setSelectedPermission(servicePermission);
    }
  }, [message?.meta?.meta?.service]);

  const handleServiceRequest = (action: "approve" | "disapprove") => {
    trackEvent("browse", {
      category: "notifications",
      entity: "Request",
      action: action === "approve" ? "Approve" : "Reject",
      location: analyticsLocation,
      ...getTrackUserContext(),
    });
    if (action === "approve") {
      const serviceWithPermission = `${
        message.meta.meta.service.split(".")[0]
      }.${selectedPermission}`;
      approveServiceRequest(message.meta.meta.id, serviceWithPermission);
    } else {
      disapproveServiceRequest(message.meta.meta.id);
    }
    setIsExpanded(false);
  };

  const toggleExpanded = async () => {
    if (isDeleting || isLoadingMedia || isDeletingRef.current) {
      return;
    }
    const entity = getNotificationEntity(notificationType);
    if (entity === "Upload" || entity === "Publish") {
      trackEvent("browse", {
        category: "notifications",
        entity,
        action: "Click",
        location: analyticsLocation,
        ...getTrackUserContext(),
      });
    }
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    if (
      newExpandedState &&
      notificationType === 5 &&
      !isDeleting &&
      !isLoadingMedia &&
      !isDeletingRef.current
    ) {
      setIsLoadingMedia(true);
      try {
        if (isDeleting || isDeletingRef.current) {
          return;
        }

        const res = await GetMediaApi({ id: message.meta.meta.id });
        if (res && res.status === 200) {
          setApiMessage(res.data);
        } else {
          console.error("API response error:", res);
        }
      } catch (error) {
        console.error("Error fetching media data:", error);
      } finally {
        setIsLoadingMedia(false);
      }
    }
  };

  const handlePublish = () => {
    if (notificationType === 5) {
      trackEvent("browse", {
        category: "notifications",
        entity: "Upload",
        action: "Publish",
        location: analyticsLocation,
        ...getTrackUserContext(),
      });
      navigate(
        `/media/${displayMessage?.id || message.meta.meta.id}/social-publish`
      );
      handleMarkAsRead();
    }
  };

  const handlePreview = () => {
    if (notificationType === 5) {
      trackEvent("browse", {
        category: "notifications",
        entity: "Upload",
        action: "View",
        location: analyticsLocation,
        ...getTrackUserContext(),
      });
      setModalContent(
        <GeneralPlayerWrapper
          media={{
            ...displayMessage,
            id: displayMessage?.id || message.meta.meta.id,
          }}
        />
      );
      setIsModalOpen(true);
    }
  };

  const handleEdit = () => {
    if (notificationType === 5) {
      trackEvent("browse", {
        category: "notifications",
        entity: "Upload",
        action: "Edit",
        location: analyticsLocation,
        ...getTrackUserContext(),
      });
      navigate(`/media/${displayMessage?.id || message.meta.meta.id}/details`);
      handleMarkAsRead();
    }
  };

  const handleView = async () => {
    // Destructure to avoid repeated property access
    const meta = message.meta?.meta;
    if (!meta) return;

    if (notificationType === 5) {
      trackEvent("browse", {
        category: "notifications",
        entity: "Upload",
        action: "Click",
        location: analyticsLocation,
        ...getTrackUserContext(),
      });
    } else if (notificationType === 6) {
      trackEvent("browse", {
        category: "notifications",
        entity: "Publish",
        action: "View",
        location: analyticsLocation,
        ...getTrackUserContext(),
      });
    } else if (notificationType === 0 || notificationType === 7) {
      trackEvent("browse", {
        category: "notifications",
        entity: "Request",
        action: "View",
        location: analyticsLocation,
        ...getTrackUserContext(),
      });
    }
    if (notificationType === 6) {
      const { status, platform, media, id, published_id, host, job_type } =
        meta;
      const isDenmark = platform === "denmark";
      const isPublished = status === 10;
      const isDenmarkEmbed = job_type === "denmark_embed";

      // Handle non-Denmark platforms that aren't published
      if (!isPublished && !isDenmark) {
        navigate(`/media/${media}/social-publish?notificationId=${id}`);
      }
      // Handle published Denmark content
      else if (isDenmark && isPublished) {
        const url = await getDenmarkMediaUrlWithSeopath(
          published_id,
          host,
          dispatch,
          callDenmarkDetailApi,
          isDenmarkEmbed,
          callDenmarkArticleGetApi,
          allHostsList
        );
        if (url) {
          window.open(url, "_blank");
        }
      }
      // Handle unpublished Denmark content
      else if (isDenmark && !isPublished) {
        navigate(`/media/${media}/details`);
      }
      // Default: open video in new tab
      else {
        openVideoInNewTab(job_type, published_id);
      }
      handleMarkAsRead();
    } else if (notificationType === 0) {
      navigate(`/users`);
      handleMarkAsRead();
      onClose?.();
    }

    onViewClick?.();
  };

  const handleMarkAsRead = () => {
    if (user && user.id) {
      readNotification({
        ids: [message.id],
        read: 1,
        // user: user.id,
        status: 0,
      })
        .then(() => {
          setIsRead(true);
        })
        .catch((error) => {
          console.error("Error marking as read:", error);
        });
    }
    setShowMoreOptions(false);
  };

  const handleDelete = () => {
    setIsDeleting(true);
    isDeletingRef.current = true;
    setShowMoreOptions(false);

    if (user && user.id) {
      readNotification({
        ids: [message.id],
        read: 1,
        // user: user.id,
        status: -1,
      })
        .then(() => {
          if (onDeleteNotification) {
            onDeleteNotification(message.id);
          }
        })
        .catch((error) => {
          setIsDeleting(false);
          isDeletingRef.current = false;
        });
    }
  };

  return (
    <div
      className={`notification-item p-3 border-bottom rounded ${
        isRead ? "fw-normal" : "fw-bold"
      }`}
      style={{
        position: "relative",
        cursor: showExpandArrow ? "pointer" : "default",
      }}
      onMouseEnter={(e) => {
        const hoverActions = e.currentTarget.querySelector(
          ".hover-actions"
        ) as HTMLElement;
        const expandedActions = e.currentTarget.querySelector(
          ".expanded-actions"
        ) as HTMLElement;
        if (hoverActions && !isExpanded) hoverActions.style.opacity = "1";
        if (expandedActions && isExpanded) expandedActions.style.opacity = "1";
      }}
      onMouseLeave={(e) => {
        const hoverActions = e.currentTarget.querySelector(
          ".hover-actions"
        ) as HTMLElement;
        const expandedActions = e.currentTarget.querySelector(
          ".expanded-actions"
        ) as HTMLElement;
        if (hoverActions && !isExpanded) hoverActions.style.opacity = "0";
        if (expandedActions && !isExpanded) expandedActions.style.opacity = "0";
        setShowMoreOptions(false);
        setShowServiceDropdown(false);
      }}
    >
      <div
        className="d-flex justify-content-between align-items-center"
        onClick={(e) => {
          // const target = e.target as HTMLElement;
          // if (e.target === e.currentTarget || !target.closest('button')) {
          if (showExpandArrow && !isDeleting && !isDeletingRef.current) {
            toggleExpanded();
            if ((notificationType === 5 || notificationType === 6) && !isRead) {
              handleMarkAsRead();
            }
            // }
          }
        }}
        style={{ cursor: showExpandArrow ? "pointer" : "default" }}
      >
        <div
          className="d-flex flex-row gap-3"
          style={{ width: "calc(100% - 90px)" }}
        >
          <div
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{
              flex: "0 0 35px",
              maxWidth: "35px",
              height: "35px",
              backgroundColor: getAvatarBgColor(),
              color: "white",
              fontSize: "12px",
              fontWeight: "bold",
              minWidth: "35px",
              minHeight: "35px",
              flexShrink: 0,
            }}
          >
            {notificationType === 6 ? (
              <PublishedIcon
                job_type={message.meta.meta?.job_type}
                className="fs-4"
                status={message.meta.meta?.status}
              />
            ) : (
              getAvatarInitials()
            )}
          </div>
          <div
            className="anchor flex-grow-1 d-flex flex-column gap-1"
            onClick={handleView}
          >
            <span className={`${!isRead ? "fw-bold text-primary" : ""}`}>
              {getAccessMessage(
                message.meta?.meta?.state,
                message.meta?.type,
                message.meta?.meta?.status
              )}
            </span>
            <span className="text-muted small">{`${formatTimestampToAgo(
              message.mt
            )}`}</span>
          </div>
        </div>
        {showEyeIcon && <Visibility onClick={handleVisibility} />}
        <div
          className="d-flex align-items-center gap-2 hover-actions mt-1 align-self-start"
          style={{
            opacity: isExpanded ? "1" : "0",
            transition: "opacity 0.2s ease-in-out",
          }}
        >
          {showViewButton && (
            <Button
              type="outline-primary"
              onClick={handleView}
              className="border"
            >
              <View size={16} />
            </Button>
          )}
          {showExpandArrow && (
            <Button
              type="outline-primary"
              onClick={(e) => {
                e.stopPropagation();
                if (!isDeleting && !isLoadingMedia && !isDeletingRef.current) {
                  toggleExpanded();
                  // Mark as read when toggling notifications (type 5 or 6) that are unread
                  if (
                    (notificationType === 5 || notificationType === 6) &&
                    !isRead
                  ) {
                    handleMarkAsRead();
                  }
                }
              }}
              className={`border ${isExpanded ? "show" : ""}`}
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </Button>
          )}
          <div
            ref={moreOptionsRef}
            className="position-relative d-inline-block"
          >
            <Button
              type="outline-primary"
              onClick={(e) => {
                e.stopPropagation();
                const entity = getNotificationEntity(notificationType);
                if (entity === "Upload" || entity === "Publish") {
                  trackEvent("browse", {
                    category: "notifications",
                    entity,
                    action: "3dot",
                    location: analyticsLocation,
                    ...getTrackUserContext(),
                  });
                }
                setShowMoreOptions(!showMoreOptions);
              }}
              className="border"
            >
              <EllipsisVertical size={16} />
            </Button>
            {/* <i
              className="bi bi-three-dots-vertical cursor-pointer fs-5 text-primary"
              onClick={(e) => {
                e.stopPropagation();
                setShowMoreOptions(!showMoreOptions);
              }}
              style={{ cursor: 'pointer', fontSize: '14px' }}
            /> */}
            {showMoreOptions && (
              <ul
                className="dropdown-menu show position-absolute"
                style={{ right: "0", top: "100%", zIndex: 1000 }}
              >
                {isUnread && (
                  <li
                    onClick={handleMarkAsRead}
                    className="text-capitalize py-1"
                  >
                    <span className="dropdown-item">
                      <i className="bi bi-check-circle me-2"></i>
                      Mark as read
                    </span>
                  </li>
                )}
                <li onClick={handleDelete} className="text-capitalize py-1">
                  <span className="dropdown-item">
                    <i className="bi bi-trash me-2"></i>
                    Delete
                  </span>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={displayMessage?.title}
          updateModal={updateModal}
          width="990px"
          closeOnOutsideClick={true}
          otherPropertiesStyle={{ maxHeight: "100%" }}
          footer={
            <div>
              <Button type="primary" onClick={handleEdit}>
                <Edit size={16} /> Edit
              </Button>
            </div>
          }
        >
          {modalContent}
        </Modal>
      )}
      {isExpanded && (
        <div className="pt-2 mt-2 border-top">
          {(notificationType === 5 || isServiceRequest) && displayMessage && (
            <div className="mt-2 d-flex flex-row justify-content-between align-items-center position-relative">
              <div className="d-flex gap-2 w-100">
                <div
                  className="ratio ratio-16x9 border bg-secondary pointer"
                  style={{ width: "100px", flex: "0 0 100px" }}
                  onClick={handlePreview}
                >
                  {!isServiceRequest && (
                    <img
                      src={
                        (isPortrait && displayMessage.images?.portrait) ||
                        displayMessage.images?.thumb
                          ? getThumbnailUrlUtil({
                              media: displayMessage,
                              id:
                                isPortrait && displayMessage.images?.portrait
                                  ? displayMessage.images.portrait
                                  : displayMessage?.images?.thumb,
                              source: displayMessage.source,
                            })
                          : noImage
                      }
                      alt="Media Thumbnail"
                      className="rounded"
                      style={{
                        objectFit: isPortrait ? "contain" : "cover",
                      }}
                    />
                  )}
                </div>
                <div
                  className={
                    isServiceRequest
                      ? "d-flex gap-2 w-100 justify-content-between align-items-center"
                      : ""
                  }
                >
                  <div
                    className="anchor fw-medium flex-grow-1 clamp-lines clamp-2"
                    onClick={handlePreview}
                  >
                    {isServiceRequest
                      ? displayMessage.name
                      : displayMessage.title}
                  </div>
                  <div className="text-muted my-2">{`${getBusinessName(
                    message.meta.meta.business
                  )} | ${getProductName(message.meta.meta.team)}`}</div>
                  {!isServiceRequest &&
                  displayMessage.media_status !== undefined ? (
                    <>
                      <span className="text-muted">
                        <strong>Created On:</strong>
                        {`${formatTimestampToAgo(message.mt)}`}
                      </span>
                      <Tooltip
                        content={mediaStatusTooltipText(
                          displayMessage.media_status
                        )}
                      >
                        <span
                          className={`${
                            mediaStatusToTextAndColor(
                              displayMessage.media_status
                            ).statusColor
                          } badge p-2 position-absolute top-0 end-0`}
                        >
                          {
                            mediaStatusToTextAndColor(
                              displayMessage.media_status
                            ).statusText
                          }
                        </span>
                      </Tooltip>
                    </>
                  ) : (
                    <div className="d-flex justify-content-between gap-2">
                      {isServiceRequest && message.meta.meta.status === 7 && (
                        <div className="d-flex flex-column gap-2">
                          <div className="text-muted small">
                            <span className="d-flex align-items-center gap-2">
                              Access:
                              <CustomSelect
                                name={`modal-permission`}
                                value={selectedPermission}
                                maintainSelectHeight="20px"
                                options={[
                                  { label: "R/O", value: "r" },
                                  { label: "R/W", value: "w" },
                                  { label: "Admin", value: "*" },
                                ]}
                                className="m-0 p-0"
                                handleChange={(selectedOption: any) => {
                                  if (selectedOption && selectedOption.value) {
                                    setSelectedPermission(selectedOption.value);
                                  }
                                }}
                              />
                            </span>
                          </div>
                          <div
                            className="btn-group"
                            role="group"
                            aria-label="Service request actions"
                          >
                            <Button
                              type="success"
                              onClick={() => handleServiceRequest("approve")}
                            >
                              Approve
                            </Button>
                            <div
                              className="btn-group"
                              role="group"
                              ref={serviceDropdownRef}
                            >
                              <button
                                className="dropdown-toggle btn btn-outline-primary text-white"
                                onClick={() =>
                                  setShowServiceDropdown(!showServiceDropdown)
                                }
                                style={{
                                  borderLeft: "1px solid rgba(255,255,255,0.3)",
                                }}
                              ></button>
                              <ul
                                className={`dropdown-menu ${
                                  showServiceDropdown ? "show" : ""
                                }`}
                                style={{
                                  position: "absolute",
                                  right: 0,
                                  top: "100%",
                                  zIndex: 1000,
                                }}
                              >
                                <li>
                                  <button
                                    className="dropdown-item text-danger"
                                    onClick={() =>
                                      handleServiceRequest("disapprove")
                                    }
                                  >
                                    <i className="bi bi-x-circle me-2 iconSvg"></i>
                                    Disapprove
                                  </button>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div
                className="d-flex gap-2 expanded-actions align-self-end"
                style={{
                  height: "30px",
                  opacity: isExpanded ? "1" : "0",
                  transition: "opacity 0.2s ease-in-out",
                }}
              >
                {displayMessage.media_status ===
                  MEDIA_STATUS.MEDIA_STATUS_PUBLISHED ||
                  (displayMessage.media_status ===
                    MEDIA_STATUS.MEDIA_STATUS_INACTIVE && (
                    <Button className="btn-sm H" onClick={handlePreview}>
                      <Eye size={16} />
                    </Button>
                  ))}
                {!isServiceRequest && (
                  <Button
                    type="outline-primary"
                    className="btn-sm"
                    onClick={handleEdit}
                  >
                    <Edit size={16} />
                  </Button>
                )}
                {displayMessage.is_mandatory && (
                  <Button
                    type="outline-primary"
                    className="btn-sm"
                    onClick={handlePublish}
                  >
                    <Send size={16} />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EachNotification;
